import { ChangeDetectionStrategy, Component, computed, inject, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet, type ActivatedRouteSnapshot, type Data } from '@angular/router';
import { UiAppShellComponent, UiTabbarComponent, type UiTabbarItem } from '@nexus/frontend-shared-ui';
import { filter, map, startWith } from 'rxjs';
import { PwaStatusService } from './core/pwa/pwa-status.service';
import { MAIN_TAB_NAVIGATION_ITEMS } from './core/shell/main-tab-navigation.constants';
import { type AppShellRouteData } from './core/shell/types/app-shell-route-data.interface';
import { type MainTabId } from './core/shell/types/main-tab-id.type';
import { type MainTabNavigationItem } from './core/shell/types/main-tab-navigation-item.interface';
import { ThemeService } from './core/theme/theme.service';
import { readWebRuntimeAuthConfig } from './core/auth/auth-runtime-config';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, UiAppShellComponent, UiTabbarComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  host: { '[class.qa-environment]': 'isQa' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  private readonly pwaStatusService: PwaStatusService = inject(PwaStatusService);
  private readonly themeService: ThemeService = inject(ThemeService);
  private readonly router: Router = inject(Router);
  private readonly document: Document = inject(DOCUMENT);
  protected readonly isQa: boolean = readWebRuntimeAuthConfig().environment === 'qa';
  private readonly shellRouteDataSignal: Signal<AppShellRouteData> = toSignal(
    this.router.events.pipe(
      filter((event: unknown): event is NavigationEnd => event instanceof NavigationEnd),
      map((): AppShellRouteData => this.readActiveShellRouteData()),
      startWith(this.readActiveShellRouteData()),
    ),
    {
      initialValue: this.readActiveShellRouteData(),
    },
  );

  protected readonly primaryTabs: readonly UiTabbarItem[] = MAIN_TAB_NAVIGATION_ITEMS;
  protected readonly isShellRoute: Signal<boolean> = computed(
    (): boolean => this.shellRouteDataSignal().shellMode === 'app-shell',
  );
  protected readonly showPrimaryTabbar: Signal<boolean> = computed((): boolean => {
    const shellRouteData: AppShellRouteData = this.shellRouteDataSignal();
    return shellRouteData.shellMode === 'app-shell' && shellRouteData.showPrimaryTabbar === true;
  });
  protected readonly activeMainTabId: Signal<string> = computed(
    (): string => this.shellRouteDataSignal().mainTabId ?? 'timeline',
  );

  public constructor() {
    const productName: string = readWebRuntimeAuthConfig().productName;
    this.document.title = this.isQa ? `${productName} — QA` : productName;
    this.themeService.initialize();
    void this.pwaStatusService.checkForUpdates();
  }

  protected handlePrimaryTabSelected(itemId: string): void {
    const selectedTab: MainTabNavigationItem | undefined = MAIN_TAB_NAVIGATION_ITEMS.find(
      (item: MainTabNavigationItem): boolean => item.id === itemId,
    );
    if (!selectedTab) {
      return;
    }

    void this.router.navigateByUrl(selectedTab.route);
  }

  private readActiveShellRouteData(): AppShellRouteData {
    let activeSnapshot: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;
    while (activeSnapshot.firstChild) {
      activeSnapshot = activeSnapshot.firstChild;
    }

    return this.toAppShellRouteData(activeSnapshot.data);
  }

  private toAppShellRouteData(routeData: Data): AppShellRouteData {
    return {
      shellMode: routeData['shellMode'] === 'app-shell' ? 'app-shell' : 'bare',
      mainTabId: this.toMainTabId(routeData['mainTabId']),
      showPrimaryTabbar: routeData['showPrimaryTabbar'] === true,
    };
  }

  private toMainTabId(value: unknown): MainTabId | undefined {
    if (
      value === 'timeline' ||
      value === 'library' ||
      value === 'people' ||
      value === 'wishlists'
    ) {
      return value;
    }

    return undefined;
  }
}
